import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import './CreditsPage.css';

interface UserCredits {
    balance: number;
    plan: string;
    monthlyCredits: number;
    usedThisMonth: number;
}

const CreditsPage: React.FC = () => {
    const [userCredits, setUserCredits] = useState<UserCredits>({
        balance: 0,
        plan: 'FREE',
        monthlyCredits: 30,
        usedThisMonth: 0,
    });
    const [syncing, setSyncing] = useState(false);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [billingMessage, setBillingMessage] = useState<string | null>(null);

    const planRank: Record<string, number> = {
        FREE: 0, Free: 0, free: 0,
        artisan: 1, ARTISAN: 1,
        studio: 2, STUDIO: 2,
        scale: 3, SCALE: 3,
    };
    const currentPlanRank = planRank[userCredits.plan] ?? 0;

    const subscriptionPlans = [
        {
            name: 'Free',
            price: 0,
            credits: 30,
            listings: 10,
            planKey: null,
            trialDays: 0,
            current: userCredits.plan === 'Free' || userCredits.plan === 'FREE',
        },
        {
            name: 'artisan',
            price: 15,
            credits: 180,
            listings: 30,
            planKey: 'artisan',
            trialDays: 3,
            current: userCredits.plan === 'artisan' || userCredits.plan === 'ARTISAN',
        },
        {
            name: 'studio',
            price: 29,
            credits: 450,
            listings: 50,
            planKey: 'studio',
            trialDays: 1,
            popular: true,
            current: userCredits.plan === 'studio' || userCredits.plan === 'STUDIO',
        },
        {
            name: 'scale',
            price: 57,
            credits: 900,
            listings: 100,
            planKey: 'scale',
            trialDays: 1,
            current: userCredits.plan === 'scale' || userCredits.plan === 'SCALE',
        },
    ];

    const fetchCredits = async () => {
        try {
            const data = await apiFetch('/api/billing/credits');
            setUserCredits(data);
        } catch (error) {
            console.error('Failed to fetch credits:', error);
        }
    };

    useEffect(() => {
        fetchCredits();

        const urlParams = new URLSearchParams(window.location.search);
        const billingSuccess = urlParams.get('billing') === 'success';
        const billingDeclined = urlParams.get('billing') === 'declined';
        const plan = urlParams.get('plan');
        const pack = urlParams.get('pack');

        if (billingDeclined) {
            setBillingMessage('Billing was not completed. No charges were made — you can try again anytime.');
            sessionStorage.removeItem('billing_pending');
            urlParams.delete('billing');
            window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        }

        const pendingBilling = sessionStorage.getItem('billing_pending');
        if (pendingBilling && !billingSuccess && !billingDeclined) {
            setBillingMessage('Subscription was not activated. You can choose a plan again anytime.');
            sessionStorage.removeItem('billing_pending');
        }

        if (billingSuccess) {
            sessionStorage.removeItem('billing_pending');
            setSyncing(true);
            apiFetch('/api/billing/sync', {
                method: 'POST',
                body: JSON.stringify({ plan, pack }),
            })
                .then(() => fetchCredits())
                .catch(err => console.error('Billing sync failed:', err))
                .finally(() => {
                    setSyncing(false);
                    urlParams.delete('billing');
                    urlParams.delete('plan');
                    urlParams.delete('pack');
                    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
                });
        }
    }, []);

    // Redirect to Shopify's native plan management page.
    // With Managed Pricing, Shopify owns the billing UI — we cannot call
    // appSubscriptionCreate directly. The webhook (app_subscriptions/update)
    // fires when the merchant approves, and syncBillingForStore updates the DB.
    // The app handle used in this URL is distinct from the API key and can
    // only be resolved live via the Admin API, so the backend fetches it.
    const openShopifyPricingPage = async () => {
        setPricingLoading(true);
        setBillingMessage(null);
        try {
            const data = await apiFetch('/api/billing/pricing-url');
            if (!data.url) throw new Error('No pricing URL returned');

            if (window.top && window.top !== window.self) {
                window.open(data.url, '_top');
            } else {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error('Failed to open pricing page:', error);
            setBillingMessage(`Unable to open the plan selection page: ${error instanceof Error ? error.message : 'Please try again.'}`);
            setPricingLoading(false);
        }
    };

    const creditsRemaining = userCredits.monthlyCredits - userCredits.usedThisMonth + userCredits.balance;
    const usagePercentage = userCredits.monthlyCredits > 0
        ? (userCredits.usedThisMonth / userCredits.monthlyCredits) * 100
        : 0;

    return (
        <div className="credits-page">
            <div className="credits-header">
                <h1>💳 Credits & Billing</h1>
                <p>Manage your AI credits and subscription</p>
                {syncing && <p className="sync-notice">Syncing billing with Shopify...</p>}
                {billingMessage && <p className="sync-notice">{billingMessage}</p>}
            </div>

            <div className="credits-balance-section">
                <div className="balance-card">
                    <div className="balance-info">
                        <span className="balance-label">Available Credits</span>
                        <span className="balance-amount">{creditsRemaining}</span>
                        <span className="balance-detail">
                            = {Math.floor(creditsRemaining / 6)} standard images or {Math.floor(creditsRemaining / 20)} pro images
                        </span>
                    </div>
                    <div className="balance-usage">
                        <div className="usage-header">
                            <span>Monthly Usage</span>
                            <span>{userCredits.usedThisMonth} / {userCredits.monthlyCredits} credits</span>
                        </div>
                        <div className="usage-bar">
                            <div
                                className="usage-fill"
                                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                            />
                        </div>
                        <span className="usage-plan">Current Plan: <strong>{userCredits.plan}</strong></span>
                    </div>
                </div>
            </div>

            <div className="credits-info-section">
                <h3>💡 Credit Usage</h3>
                <div className="credits-info-grid">
                    <div className="info-item">
                        <span className="info-icon">📝</span>
                        <span className="info-text">AI Text Listing</span>
                        <span className="info-cost">FREE with plan</span>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">🖼️</span>
                        <span className="info-text">Standard AI Image</span>
                        <span className="info-cost">6 credits</span>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">✨</span>
                        <span className="info-text">Pro AI Image</span>
                        <span className="info-cost">20 credits</span>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">🎭</span>
                        <span className="info-text">3D Model</span>
                        <span className="info-cost">50 credits</span>
                    </div>
                </div>
            </div>

            <div className="subscription-section">
                <h2>📋 Subscription Plans</h2>
                <p>Get more monthly credits with a subscription</p>

                <div className="plans-grid">
                    {subscriptionPlans.map((plan) => (
                        <div
                            key={plan.name}
                            className={`plan-card ${plan.popular ? 'popular' : ''} ${plan.current ? 'current' : ''}`}
                        >
                            {plan.popular && <span className="popular-tag">Best Value</span>}
                            {plan.current && <span className="current-tag">Current Plan</span>}
                            <h3>{plan.name}</h3>
                            <div className="plan-price">
                                ${plan.price}<span>/month</span>
                            </div>
                            {plan.trialDays > 0 && (
                                <div className="plan-trial">{plan.trialDays}-day free trial</div>
                            )}
                            <ul className="plan-features">
                                <li>✓ {plan.credits} credits/month</li>
                                <li>✓ {plan.listings} AI listings</li>
                            </ul>
                            {plan.current ? (
                                <button className="plan-btn current" disabled>
                                    Current Plan
                                </button>
                            ) : (
                                <button
                                    className="plan-btn"
                                    onClick={openShopifyPricingPage}
                                    disabled={pricingLoading}
                                >
                                    {pricingLoading
                                        ? 'Loading...'
                                        : !plan.planKey
                                            ? 'Downgrade to Free'
                                            : (planRank[plan.planKey] ?? 0) > currentPlanRank
                                                ? 'Upgrade'
                                                : 'Switch Plan'}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CreditsPage;
